/**
 * SwiftAffiliate Integration Script
 * 
 * This script manages affiliate-related functionalities, including:
 * - Checking if a customer is registered as an affiliate
 * - Saving the affiliate code in session storage
 * - Creating a dashboard redirect button
 * - Creating a share affiliate link button
 * 
 * The script ensures seamless integration with SwiftAffiliate API.
 */

document.addEventListener("DOMContentLoaded", function () {
    // Define constants from the global swiftAffiliate object (if available)
    const swiftAffiliateBaseUrl = window.swiftAffiliate?.base_url;
    const swiftAffiliateDashboardPath = window.swiftAffiliate?.dashboard_path;
    const swiftAffiliateToken = window.swiftAffiliate?.token;
    const swiftAffiliateWebsiteCode = window.swiftAffiliate?.website_code;
    const customerEmail = window.swiftAffiliate?.customer_email;
    const customerFirstname = window.swiftAffiliate?.customer_firstname;
    const customerLastname = window.swiftAffiliate?.customer_lastname;

    /**
     * Show toast notification (auto-hide after 3 seconds)
     * @param {string} message - The message to display
     * @param {string} type - Type of notification: 'success' or 'error'
     */
    function showToast(message, type = 'success') {
        // Create toast container
        const toast = document.createElement('div');
        toast.className = 'swift-affiliate-toast';
        toast.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: ${type === 'success' ? '#4CAF50' : '#f44336'};
            color: white;
            padding: 16px 24px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            z-index: 10000;
            font-size: 14px;
            font-weight: 500;
            max-width: 350px;
            animation: slideInRight 0.3s ease-out;
        `;
        toast.textContent = message;

        // Add animation keyframes if not exists
        if (!document.querySelector('#swift-affiliate-toast-styles')) {
            const style = document.createElement('style');
            style.id = 'swift-affiliate-toast-styles';
            style.textContent = `
                @keyframes slideInRight {
                    from {
                        transform: translateX(400px);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                @keyframes slideOutRight {
                    from {
                        transform: translateX(0);
                        opacity: 1;
                    }
                    to {
                        transform: translateX(400px);
                        opacity: 0;
                    }
                }
            `;
            document.head.appendChild(style);
        }

        // Append to body
        document.body.appendChild(toast);

        // Auto remove after 3 seconds with animation
        setTimeout(() => {
            toast.style.animation = 'slideOutRight 0.3s ease-in';
            setTimeout(() => {
                toast.remove();
            }, 300);
        }, 3000);
    }

    /**
     * Retrieves the product SKU from the page using multiple fallback strategies.
     * @returns {string} The SKU string, or empty string if not found.
     */
    function getSku() {
        // Strategy 1: Shopify Analytics global object (most reliable)
        const analyticsVariants = window.ShopifyAnalytics?.meta?.product?.variants;
        if (analyticsVariants?.length) {
            const sku = analyticsVariants[0]?.sku;
            if (sku) return sku;
        }

        // Strategy 2: meta product JSON (common in Shopify themes)
        try {
            const productJson = document.querySelector('[data-product-json]')?.textContent
                || document.getElementById('ProductJson')?.textContent
                || document.getElementById('product-json')?.textContent;
            if (productJson) {
                const product = JSON.parse(productJson);
                const sku = product?.variants?.[0]?.sku;
                if (sku) return sku;
            }
        } catch (e) {}

        // Strategy 3: Common theme SKU element selectors
        const skuSelectors = [
            '[data-product-sku]',
            '[data-sku]',
            '.product__sku .sku',
            '.product-meta__sku-number',
            '.product-single__sku',
            '.variant-sku',
            '[class*="product-sku"] span',
            '[class*="sku-number"]',
            '[class*="sku"] span',
        ];

        for (const selector of skuSelectors) {
            const el = document.querySelector(selector);
            if (el?.textContent?.trim()) return el.textContent.trim();
        }

        // Strategy 4: Parse SKU from visible text matching "SKU : XXXXXX" pattern
        const allElements = document.querySelectorAll('p, span, div, td, li');
        for (const el of allElements) {
            const text = el.childNodes.length === 1 ? el.textContent.trim() : '';
            const match = text.match(/^SKU\s*[:\-]\s*(.+)$/i);
            if (match) return match[1].trim();
        }

        return "";
    }

    /**
     * Checks if the current customer is registered as an affiliate.
     * @returns {Promise<boolean>} True if the customer is registered, otherwise false.
     */
    async function checkAffiliateRegistration() {
        try {
            const response = await fetch(swiftAffiliateBaseUrl + "/graphql", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer " + swiftAffiliateToken,
                },
                body: JSON.stringify({
                    query: `
                        query {
                            getBrandsByAffiliateEmail(input: { email: "${customerEmail}" }) {
                                website_code
                                website_name
                            }
                        }
                    `,
                }),
            });

            const data = await response.json();

            if (data.errors) {
                return false;
            }

            const brands = data?.data?.getBrandsByAffiliateEmail || [];
            return brands.some(brand => brand.website_code === swiftAffiliateWebsiteCode);
        } catch (error) {
            return false;
        }
    }

    /**
     * Injects affiliate data into Shopify cart attributes.
     */
    function injectCartAttributes() {
        const affiliateCode = sessionStorage.getItem('affiliate_code');
        const affiliateSku = sessionStorage.getItem('affiliate_sku');

        if (!affiliateCode) return;

        fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                attributes: {
                    affiliate_code: affiliateCode,
                    affiliate_sku: affiliateSku || ""
                }
            })
        });
    }

    /**
     * Clears affiliate data from sessionStorage and cart attributes
     * after order is placed to prevent reuse/abuse.
     */
    function clearAffiliateSession() {
        sessionStorage.removeItem('has_affiliate_code');
        sessionStorage.removeItem('affiliate_code');
        sessionStorage.removeItem('affiliate_sku');

        // Also clear from cart attributes so it doesn't persist into next checkout
        fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                attributes: {
                    affiliate_code: "",
                    affiliate_sku: ""
                }
            })
        });
    }

    /**
     * Detects if the current page is a Shopify thank you / order confirmation page
     * and clears affiliate session upon successful order placement.
     */
    function handlePostOrderCleanup() {
        const isThankYouPage = window.location.pathname.includes('/thank_you')
            || window.location.pathname.includes('/orders/')
            || window.Shopify?.Checkout?.step === 'thank_you'
            || document.querySelector('.os-order-number') !== null
            || document.querySelector('[data-order-id]') !== null;

        if (isThankYouPage) {
            clearAffiliateSession();
        }
    }

    /**
     * Saves the affiliate code from URL parameters into sessionStorage
     * and injects it into Shopify cart attributes.
     */
    function saveAffiliateCode() {
        const urlParams = new URLSearchParams(window.location.search);
        const affiliateCode = urlParams.get("affc");

        if (affiliateCode) {
            sessionStorage.setItem('has_affiliate_code', true);
            sessionStorage.setItem('affiliate_code', affiliateCode);

            // Extract and save SKU separately if present
            const parts = affiliateCode.split('_');
            if (parts.length > 1) {
                const sku = parts[parts.length - 1];
                sessionStorage.setItem('affiliate_sku', sku);
            }

            injectCartAttributes();
        }
    }

    /**
     * Creates and inserts a dashboard redirect button for affiliates.
     * Matches 9to9 implementation - generates URL immediately and sets as href.
     */
    function createRedirectDashboardButton() {
        // Check if customer is logged in
        if (!customerEmail) {
            return;
        }

        let attempts = 0;
        const maxAttempts = 5;

        async function tryFindingButton() {
            // Find the "View addresses" link in account page
            let addressButton = document.querySelector('a[href="/account/addresses"]') ||
                               document.querySelector('a[href*="account/addresses"]') ||
                               document.querySelector('a[href*="https://scv2-pwa-rajasusu.sandbox.id/dashboard"]') ||
                               document.getElementById("address-link");

            if (addressButton) {
                try {
                    // Generate affiliate dashboard login URL
                    const response = await fetch(swiftAffiliateBaseUrl + "/graphql", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": "Bearer " + swiftAffiliateToken,
                        },
                        body: JSON.stringify({
                            query: `
                                mutation {
                                    generateLoginUrl(
                                        input: {
                                            email: "${customerEmail}"
                                            website_code: "${swiftAffiliateWebsiteCode}"
                                            firstname: "${customerFirstname}"
                                            lastname: "${customerLastname}"
                                        }
                                    )
                                }
                            `,
                        }),
                    });

                    const data = await response.json();
                    const redirectUrl = data?.data?.generateLoginUrl;

                    if (redirectUrl) {
                        // Clone address button to match sidebar styling
                        const clonedButton = addressButton.cloneNode(true);
                        clonedButton.id = "swift-affiliate-dashboard";
                        clonedButton.textContent = "Affiliate Program";
                        clonedButton.href = redirectUrl;
                        clonedButton.setAttribute("target", "_blank");
                        clonedButton.setAttribute("rel", "noopener noreferrer");
                        clonedButton.classList.add("affiliate-dashboard-link");

                        // Insert after loyalty link (9to9 pattern: inject after wallet, but we don't have wallet)
                        const loyaltyLink = document.getElementById("loyalty-link");
                        if (loyaltyLink) {
                            loyaltyLink.parentNode.insertBefore(clonedButton, loyaltyLink.nextSibling);
                        }
                    }
                } catch (error) {
                    // Error generating dashboard URL
                }
            } else if (attempts < maxAttempts) {
                attempts++;
                setTimeout(tryFindingButton, 1000);
            }
        }

        tryFindingButton();
    }

    /**
     * Creates and inserts an affiliate share button.
     */
    async function createShareButton() {
        // Check customer email first
        if (!customerEmail) {
            return;
        }

        // Check affiliate registration
        const isRegistered = await checkAffiliateRegistration();

        if (!isRegistered) {
            return;
        }

        // Find button container
        const buttonContainer = document.querySelector('.product-form__buttons');

        if (!buttonContainer) {
            return;
        }

        // Skip product gift (pickup-only) products
        const productForm = buttonContainer.closest('.product-form');
        if (productForm && productForm.dataset.productGift === 'Yes') {
            return;
        }

        // Find button to clone - try payment button first, fallback to submit button
        let buttonToClone = buttonContainer.querySelector('div[data-shopify="payment-button"] button');
        if (!buttonToClone) {
            buttonToClone = buttonContainer.querySelector('button[type="submit"]');
        }

        if (!buttonToClone) {
            return;
        }

        // Hide affiliate button when product is out of stock
        const submitButton = buttonContainer.querySelector('button[type="submit"]');
        if (submitButton && submitButton.disabled) {
            return;
        }

        // Create share button
        const shareButton = buttonToClone.cloneNode(true);
        shareButton.id = "swift-affiliate-share-button";
        shareButton.type = "button"; // Important: prevent form submission
        shareButton.removeAttribute("disabled");
        shareButton.removeAttribute("name");

        // Add icon + text content
        const linkIconSVG = `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 8px;">
<path d="M11.0088 3.80906C11.8875 2.93038 13.3121 2.93038 14.1908 3.80906C15.0695 4.68774 15.0695 6.11236 14.1908 6.99104L13.0889 8.09294C12.8253 8.35654 12.8253 8.78393 13.0889 9.04753C13.3525 9.31114 13.7799 9.31114 14.0435 9.04753L15.1454 7.94563C16.5513 6.53975 16.5513 4.26035 15.1454 2.85446C13.7395 1.44858 11.4601 1.44858 10.0542 2.85446L7.35422 5.55446C5.94833 6.96035 5.94833 9.23975 7.35422 10.6456C7.41985 10.7113 7.48747 10.7739 7.55693 10.8335C7.83977 11.0764 8.26592 11.044 8.50877 10.7611C8.75162 10.4783 8.7192 10.0521 8.43636 9.80928C8.39281 9.77188 8.35025 9.73248 8.30881 9.69104C7.43013 8.81236 7.43013 7.38774 8.30881 6.50906L11.0088 3.80906Z" fill="#CC0100"/>
<path d="M10.4427 7.16655C10.1598 6.9237 9.73369 6.95612 9.49084 7.23896C9.24799 7.5218 9.28041 7.94796 9.56325 8.19081C9.6068 8.2282 9.64936 8.26761 9.6908 8.30905C10.5695 9.18773 10.5695 10.6124 9.6908 11.491L6.9908 14.191C6.11212 15.0697 4.68749 15.0697 3.80881 14.191C2.93013 13.3123 2.93013 11.8877 3.80881 11.009L4.91072 9.90715C5.17432 9.64354 5.17432 9.21616 4.91072 8.95255C4.64711 8.68895 4.21973 8.68895 3.95612 8.95255L2.85422 10.0545C1.44833 11.4603 1.44833 13.7397 2.85422 15.1456C4.26011 16.5515 6.5395 16.5515 7.94539 15.1456L10.6454 12.4456C12.0513 11.0397 12.0513 8.76034 10.6454 7.35445C10.5798 7.28883 10.5121 7.22619 10.4427 7.16655Z" fill="#CC0100"/>
</svg>`;
        shareButton.innerHTML = linkIconSVG + "Share Affiliate Link";

        // Adjust button styling
        shareButton.classList.remove("button--secondary");
        shareButton.classList.remove("button--primary");
        shareButton.classList.remove("button--full-width");
        shareButton.style.cssText = `
            background: transparent;
            color: #CC0100;
            border: none;
            padding: 0;
            font-size: 14px;
            font-weight: 400;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: opacity 0.3s ease;
        `;
        shareButton.addEventListener('mouseenter', () => shareButton.style.opacity = '0.7');
        shareButton.addEventListener('mouseleave', () => shareButton.style.opacity = '1');

        // Add click event listener
        shareButton.addEventListener("click", async (e) => {
            e.preventDefault();
            e.stopPropagation();

            const shopUrl = window.swiftAffiliate?.shop_url;
            const currentPageUrl = window.location.href.split('?')[0];

            try {
                const response = await fetch(swiftAffiliateBaseUrl + "/graphql", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + swiftAffiliateToken,
                    },
                    body: JSON.stringify({
                        query: `
                            mutation {
                                generateAffiliatePromoLink(
                                    input: {
                                        email: "${customerEmail}"
                                        website_code: "${swiftAffiliateWebsiteCode}"
                                        basic_url: "${shopUrl}"
                                        traffic_source: "no_source+1"
                                    }
                                ) {
                                    affiliate_link_code
                                }
                            }
                        `,
                    }),
                });

                const data = await response.json();

                const affiliateCode = data?.data?.generateAffiliatePromoLink?.affiliate_link_code;

                if (affiliateCode) {
                    // Get SKU and append to affiliate code
                    const sku = getSku();
                    const affcValue = sku ? `${affiliateCode}_${sku}` : affiliateCode;
                    const affiliateUrl = `${currentPageUrl}?affc=${encodeURIComponent(affcValue)}`;

                    await navigator.clipboard.writeText(affiliateUrl);
                    showToast("Affiliate URL copied to clipboard!", "success");
                } else {
                    showToast("Failed to generate affiliate link. Please try again.", "error");
                }
            } catch (error) {
                showToast("Error generating affiliate link.", "error");
            }
        });

        // Create wrapper div for centered placement
        const shareButtonWrapper = document.createElement('div');
        shareButtonWrapper.style.cssText = 'width: 100%; margin-top: 10px; text-align: center;';
        shareButtonWrapper.appendChild(shareButton);

        // Insert AFTER button container (separate row)
        buttonContainer.parentNode.insertBefore(shareButtonWrapper, buttonContainer.nextSibling);

        // Toggle visibility when variant changes (disabled = OOS)
        if (submitButton) {
            new MutationObserver(() => {
                shareButtonWrapper.style.display = submitButton.disabled ? 'none' : '';
            }).observe(submitButton, { attributes: true, attributeFilter: ['disabled'] });
        }
    }

    createRedirectDashboardButton();
    createShareButton();
    saveAffiliateCode();
    handlePostOrderCleanup();
});